import { useEffect, useMemo, useState } from 'react';

import { Table, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useHistory, useParams } from 'react-router-dom';

import { ApiError, handleError } from '../../../api/base';
import UserService from '../../../api/user';
import { useAuth } from '../../../hooks/useAuth';
import { UserStreamSessionModel } from '../../../models/user-stream-session';
import { useAppDispatch, useAppSelector } from '../../../store';
import { getUser } from '../../../store/features/users/usersSlice';
import { formatDate, UserPermissions } from '../../../util';
import Button from '../../elements/Button';
import NoPermission from '../../elements/NoPermission';
import ViewWrapper from '../../elements/ViewWrapper';

const { Text } = Typography;

const UserStreamSessionsView = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAuth();
  const { user, fetchingUser } = useAppSelector((state) => state.users);

  const [sessions, setSessions] = useState<UserStreamSessionModel[]>([]);
  const [fetchingSessions, setFetchingSessions] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const canViewStreamSessions = hasPermission(UserPermissions.UsersEdit);

  useEffect(() => {
    if (id) {
      dispatch(getUser(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    let isMounted = true;

    const fetchStreamSessions = async () => {
      if (!id) {
        return;
      }

      setFetchingSessions(true);
      try {
        const resp = await UserService().listUserStreamSessions(id);
        if (isMounted) {
          setSessions(resp.data || []);
        }
      } catch (error) {
        handleError(error as ApiError);
      } finally {
        if (isMounted) {
          setFetchingSessions(false);
        }
      }
    };

    if (canViewStreamSessions) {
      fetchStreamSessions();
    }

    return () => {
      isMounted = false;
    };
  }, [id, canViewStreamSessions]);

  const pagedSessions = useMemo(() => {
    const offset = (page - 1) * pageSize;
    return sessions.slice(offset, offset + pageSize);
  }, [sessions, page, pageSize]);

  const columns: ColumnsType<UserStreamSessionModel> = useMemo(
    () => [
      {
        title: 'No.',
        key: 'index',
        width: 70,
        render: (_value: unknown, _record: UserStreamSessionModel, index) =>
          (page - 1) * pageSize + index + 1,
      },
      {
        title: 'Session UUID',
        dataIndex: 'uuid',
        key: 'uuid',
        render: (uuid: string) => (
          <Text ellipsis style={{ maxWidth: 420 }}>
            {uuid}
          </Text>
        ),
      },
      {
        title: 'Created',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 240,
        render: (createdAt?: string) =>
          createdAt ? formatDate(createdAt, undefined, 'America/Chicago') : 'N/A',
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 200,
        align: 'right',
        render: (_value: unknown, record: UserStreamSessionModel) => (
          <Button
            onClick={() =>
              history.push(`/users/${id}/stream-sessions/${record.uuid}/logs`)
            }
          >
            View session logs
          </Button>
        ),
      },
    ],
    [history, id, page, pageSize]
  );

  if (!canViewStreamSessions) {
    return <NoPermission />;
  }

  return (
    <ViewWrapper
      options={{
        goBack: true,
        loading: fetchingUser,
      }}
      headerTitle={user?.name ? `${user.name} Stream Sessions` : 'Stream Sessions'}
    >
      <Table
        loading={fetchingSessions}
        rowKey={(session) => `${session.uuid}-row-key`}
        columns={columns}
        dataSource={pagedSessions}
        pagination={{
          current: page,
          pageSize,
          total: sessions.length,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage);
            setPageSize(nextPageSize);
          },
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        }}
      />
      <div style={{ marginTop: 16 }}>
        <Button onClick={() => history.push(`/users/${id}`)}>Back to User</Button>
      </div>
    </ViewWrapper>
  );
};

export default UserStreamSessionsView;
